import type { SelectOption } from "@cinnamon/design-system/types";
import { makeIdTemp } from "@cinnamon/design-system/utils";
import find from "lodash-es/find";
import keys from "lodash-es/keys";

import { EXEC_TYPE, FORM_TYPE } from "@/constants/global";
import { CONVERT_DATE_FORMAT_OPTIONS } from "@/pages/PipelineConfiguration/components/DataEnhancement/components/ExpandRowContent/ConvertDateFormatRow/constants";
import {
  type AutoClassificationPipeline,
  type CONVERT_BYTE_STRING_MODE,
  type ClassificationPipeline,
  type ConvertByteStringValue,
  type DataEnhancementServer,
  type DataEnhancementValue,
  type DetectableClass,
  type DetectableClassServer,
  type Document,
  FORMAT_TEXT_TYPE_DATA_ENHANCEMENT,
  type FormatTextValue,
  type IDataEnhancement,
  type IFieldInfo,
  type OcrPipeline,
  type PipelineVersion,
  type PipelineVersionServer,
  STEP_TYPE_DATA_ENHANCEMENT,
  type ServerClassificationPipeline,
  type ServerOcrPipeline,
  type Target,
} from "@/types/pipeline";

export const normalizeDetectableClass = (detectableClasses: DetectableClassServer[]): DetectableClass[] => {
  return detectableClasses.map((detectableClass) => ({
    ...detectableClass,
    id: makeIdTemp(),
  }));
};

export const transformDetectableClassesToPayload = (detectableClasses: DetectableClass[]): DetectableClassServer[] =>
  detectableClasses.map(({ class_id: classId, class_name: className, description }) => ({
    class_id: classId,
    class_name: className,
    description,
  }));

const normalizeFixedFormPipeline = (serverPipeline: ServerOcrPipeline): OcrPipeline => {
  return {
    ...serverPipeline,
    isSelected: false,
    targets: keys(serverPipeline.targets).map((classId) => ({
      classId,
      ...serverPipeline.targets[classId],
    })),
  };
};
const normalizeRegularOcrPipeline = (serverPipeline: ServerOcrPipeline): OcrPipeline => {
  return {
    ...serverPipeline,
    isSelected: false,
    targets: keys(serverPipeline.targets).map((classId, order) => ({
      classId,
      order,
      ...serverPipeline.targets[classId],
    })),
  };
};
const normalizeClassificationPipeline = (serverPipeline: ServerClassificationPipeline): ClassificationPipeline => {
  return {
    ...serverPipeline,
    isSelected: false,
    detectable_classes: normalizeDetectableClass(serverPipeline.detectable_classes ?? []),
  };
};

const normalizeAutoClassificationPipeline = (
  serverPipeline: AutoClassificationPipeline
): AutoClassificationPipeline => {
  return {
    ...serverPipeline,
    isSelected: false,
    class_configs: serverPipeline.class_configs ?? [],
  };
};

/*
 * Helper for exhaustive checks
 */
const assertNever = (x: never): never => {
  throw new Error(`Unhandled pipeline variant: ${JSON.stringify(x)}`);
};
export const normalizePipeline = (
  serverPipeline: ServerOcrPipeline | ServerClassificationPipeline | AutoClassificationPipeline
): ClassificationPipeline | AutoClassificationPipeline | OcrPipeline => {
  switch (serverPipeline.exec_type) {
    case EXEC_TYPE.OCR:
      return serverPipeline.form_type === FORM_TYPE.FIXED_FORM
        ? normalizeFixedFormPipeline(serverPipeline)
        : normalizeRegularOcrPipeline(serverPipeline);
    case EXEC_TYPE.CLASSIFICATION:
      switch (serverPipeline.form_type) {
        case FORM_TYPE.AUTO_CLASSIFICATION:
          // If types are improved (discriminated by form_type), cast can be removed
          return normalizeAutoClassificationPipeline(serverPipeline as AutoClassificationPipeline);
        default:
          return normalizeClassificationPipeline(serverPipeline);
      }
    default:
      return assertNever(serverPipeline);
  }
};

const convertByteStringValue = (mode?: CONVERT_BYTE_STRING_MODE | null): ConvertByteStringValue => {
  if (!mode) return [null];
  return [mode];
};

export const getProcessorsValueFromServer = (
  postProcessor: DataEnhancementServer,
  targetsObj: Record<string, Document>
  // eslint-disable-next-line complexity
): DataEnhancementValue => {
  switch (postProcessor.action) {
    case STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE:
    case STEP_TYPE_DATA_ENHANCEMENT.FIND_AND_REPLACE_REGEX: {
      /*
       * TODO: refactor after release
       * compatible with old data
       */
      const findValue = typeof postProcessor.find === "string" ? [postProcessor.find] : postProcessor.find;
      return [findValue || [""], postProcessor.replace ? [postProcessor.replace] : [""]];
    }
    case STEP_TYPE_DATA_ENHANCEMENT.FORMAT_TEXT: {
      return (postProcessor?.template || []).reduce<FormatTextValue>((result, item) => {
        if (!item) {
          return result;
        }

        if (item.type === FORMAT_TEXT_TYPE_DATA_ENHANCEMENT.LITERAL) {
          return [...result, item.value as string];
        }

        return [
          ...result,
          { value: item.value as string, label: targetsObj[item.value]?.display_name } as SelectOption,
        ];
      }, []);
    }

    case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_DATE_FORMAT: {
      return [
        find(CONVERT_DATE_FORMAT_OPTIONS, { value: postProcessor.date_format }) as SelectOption,
        postProcessor.return_original_if_fails ? "true" : "false",
        postProcessor.complete_missing_year ?? null,
      ];
    }

    case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_BYTE_STRING: {
      return convertByteStringValue(postProcessor.mode);
    }

    case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_UPPER_CASE:
    case STEP_TYPE_DATA_ENHANCEMENT.CONVERT_TO_LOWER_CASE: {
      return [];
    }

    default:
      return [postProcessor.remove_all ? "true" : "false"];
  }
};

export const normalizePipelineVersion = (pipelineVersionServer: PipelineVersionServer): PipelineVersion => {
  const isClassification = pipelineVersionServer.pipeline.exec_type === EXEC_TYPE.CLASSIFICATION;

  if (isClassification) {
    return {
      ...pipelineVersionServer,
      detectable_classes: normalizeDetectableClass(pipelineVersionServer.detectable_classes || []),
      class_configs: pipelineVersionServer.class_configs ?? [],
      targets: [],
      post_processors: [],
    };
  }

  const pipelineVersion = pipelineVersionServer;
  const targets = keys(pipelineVersion.targets).map((classId, order) => ({
    classId,
    order,
    ...pipelineVersion.targets[classId],
  }));
  const targetsObj = targets.reduce((result: Record<string, Target>, item: Target) => {
    return { ...result, [item.id || ""]: item };
  }, {});
  let isOldDataEnhancementFormat = false;
  return {
    ...pipelineVersion,
    detectable_classes: [],
    targets,
    post_processors: ((pipelineVersion.post_processors as unknown as DataEnhancementServer[]) || []).map(
      (item: DataEnhancementServer): IDataEnhancement => {
        const action = (item.action as STEP_TYPE_DATA_ENHANCEMENT) ?? null;

        if (typeof item.output === "string") {
          isOldDataEnhancementFormat = true;
        }

        /*
         * TODO: refactor after release
         * compatible with old data
         */
        const output: string[] = typeof item.output === "string" ? [item.output] : item.output;
        return {
          id: item.id || makeIdTemp(),
          action,
          input: item.input.reduce((result: IFieldInfo[], id) => {
            if (targetsObj[id]) {
              return [
                ...result,
                {
                  id,
                  display_name: targetsObj[id].display_name,
                  label: `${targetsObj[id].display_name} (${targetsObj[id].classId})`,
                  type: targetsObj[id].type,
                },
              ];
            }
            return result;
          }, []),
          output: output.reduce((result: IFieldInfo[], id: string) => {
            if (targetsObj[id]) {
              return [
                ...result,
                {
                  id,
                  display_name: targetsObj[id].display_name,
                  label: `${targetsObj[id].display_name} (${targetsObj[id].classId})`,
                  type: targetsObj[id].type,
                },
              ];
            }
            return result;
          }, []),
          value: getProcessorsValueFromServer(item, targetsObj),
          description: item.description || "",
        } as IDataEnhancement;
      }
    ),
    isOldDataEnhancementFormat,
  };
};
